import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useStore } from "../../helpers";
import { IconButton } from "@mui/material";
import { AgGridReact } from "@ag-grid-community/react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { ServiceRequestDelete } from "./service-request-delete";
import { ServiceFilteredBy } from "./sr-components/sr-selected-filter";

import {
    ServiceRequestCard,
    StatusBadge,
    ServiceRequestCardSkeleton,
    ServiceRequestTableSkeleton,
    SatisfactionIcon,
} from "./sr-components";

import { ENABLE_SAT_COLUMN } from "./sr-constants";
import { SideOverlay } from "../../components";

type Mode = "MY_REQUESTS" | "ALL_REQUESTS";

type Props = {
    mode: Mode;
    viewMode: "card" | "table";
    faceSelectedRequest?: any[] | null;
    onClearFaceFilter?: () => void;
};

const rowStyle = { cursor: "pointer" };

export const ServiceRequestsList = observer(
    ({ mode, viewMode, faceSelectedRequest, onClearFaceFilter }: Props) => {
        const { projectid } = useParams();
        const rootStore = useStore();
        const { serviceRequestsStore } = rootStore;

        const { searchQuery, activeServiceFilter } = serviceRequestsStore;
        const getSafeText = (value: any, fallback = "N/A"): string => {
            if (value === null || value === undefined || value === "") {
                return fallback;
            }

            if (typeof value === "string" || typeof value === "number") {
                return String(value);
            }

            if (typeof value === "boolean") {
                return value ? "Yes" : "No";
            }

            if (typeof value === "object") {
                // Try the most useful display fields
                if (typeof value.name === "string") {
                    return value.name;
                }

                if (typeof value.display === "string") {
                    return value.display;
                }

                if (typeof value.label === "string") {
                    return value.label;
                }

                if (typeof value.status === "string") {
                    return value.status;
                }

                if (typeof value.address === "string") {
                    return value.address;
                }

                if (typeof value.location === "string") {
                    return value.location;
                }

                if (typeof value.description === "string") {
                    return value.description;
                }

                if (typeof value.type === "string") {
                    return value.type;
                }

                return fallback;
            }

            return fallback;
        };
        const RequestDetail = ({ request }: { request: any }) => {
            // ============================================================
            // REQUEST TYPE
            // ============================================================

            const requestName = getSafeText(
                request?.type?.type?.name || request?.type?.name || request?.type
            );

            // ============================================================
            // TRACKING ID
            // ============================================================

            const trackingId = getSafeText(request?.trackingid);

            // ============================================================
            // STATUS
            // ============================================================

            const status = {
                status: getSafeText(request?.status?.status, "N/A"),
                color: typeof request?.status?.color === "string" ? request.status.color : "#6B7280",
            };

            // ============================================================
            // DATES
            // ============================================================

            const createdDate = request?.created
                ? dayjs(request.created).format("MMM D, YYYY hh:mm A")
                : "N/A";

            const modifiedDate = request?.modified
                ? dayjs(request.modified).format("MMM D, YYYY hh:mm A")
                : "N/A";

            // ============================================================
            // ASSIGNEE
            // ============================================================

            const assignedTo = getSafeText(
                request?.assignee?.is_office_person
                    ? request?.assignee?.user_profile?.name
                    : request?.assignee?.assignee?.name,
                "Unassigned"
            );

            // ============================================================
            // PRIORITY
            // ============================================================

            const priority = getSafeText(
                request?.priority?.name ||
                    request?.priority?.priority ||
                    request?.priority?.label ||
                    request?.priority
            );

            // ============================================================
            // NODE
            // ============================================================

            const node = getSafeText(request?.nodeid || request?.node);

            // ============================================================
            // BENEFICIARY
            // ============================================================

            const beneficiaryName = getSafeText(request?.subject?.subject?.profile?.name_eng);

            const beneficiaryPhone = getSafeText(request?.subject?.subject?.whatsapp);

            // ============================================================
            // LOCATION
            // ============================================================

            const locationData = request?.location;

            const locationAddress = getSafeText(
                locationData?.address || locationData?.location || locationData
            );

            const locationDescription = getSafeText(locationData?.description, "");

            // ============================================================
            // DESCRIPTION
            // ============================================================

            const description = getSafeText(request?.description, "");

            return (
                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    {/* ====================================================
                REQUEST HEADER
            ==================================================== */}

                    <div className="border-b border-gray-200 bg-gray-50 p-4">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                                    Service Request
                                </p>

                                <h3 className="mt-1 text-lg font-semibold text-gray-900">{requestName}</h3>

                                <p className="mt-1 font-mono text-xs text-gray-500">{trackingId}</p>
                            </div>

                            <StatusBadge status={status} size="large" />
                        </div>
                    </div>

                    {/* ====================================================
                BENEFICIARY
            ==================================================== */}

                    <div className="border-b border-gray-100 p-4">
                        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                            Beneficiary
                        </p>

                        <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50">
                                <i className="fas fa-user text-blue-600" />
                            </div>

                            <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-gray-900">
                                    {beneficiaryName}
                                </p>

                                <p className="mt-0.5 text-xs text-gray-500">{beneficiaryPhone}</p>
                            </div>
                        </div>
                    </div>

                    {/* ====================================================
                REQUEST INFORMATION
            ==================================================== */}

                    <div className="p-4">
                        <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-gray-400">
                            Request Information
                        </p>

                        <div className="grid grid-cols-2 gap-x-5 gap-y-5">
                            {/* Status */}

                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                    Status
                                </p>

                                <div className="mt-1">
                                    <StatusBadge status={status} size="small" />
                                </div>
                            </div>

                            {/* Tracking */}

                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                    Tracking ID
                                </p>

                                <p className="mt-1 break-all font-mono text-xs font-semibold text-gray-800">
                                    {trackingId}
                                </p>
                            </div>

                            {/* Request Type */}

                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                    Request Type
                                </p>

                                <p className="mt-1 text-sm font-medium text-gray-800">{requestName}</p>
                            </div>

                            {/* Priority */}

                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                    Priority
                                </p>

                                <p className="mt-1 text-sm font-medium text-gray-800">{priority}</p>
                            </div>

                            {/* Created */}

                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                    Created
                                </p>

                                <p className="mt-1 text-sm font-medium text-gray-800">{createdDate}</p>
                            </div>

                            {/* Modified */}

                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                    Last Updated
                                </p>

                                <p className="mt-1 text-sm font-medium text-gray-800">{modifiedDate}</p>
                            </div>

                            {/* Assigned */}

                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                    Assigned To
                                </p>

                                <p className="mt-1 text-sm font-medium text-gray-800">{assignedTo}</p>
                            </div>

                            {/* Node */}

                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                    Node
                                </p>

                                <p className="mt-1 text-sm font-medium text-gray-800">{node}</p>
                            </div>
                        </div>

                        {/* ====================================================
                    LOCATION
                ==================================================== */}

                        <div className="mt-5 border-t border-gray-100 pt-4">
                            <p className="mb-3 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                Location
                            </p>

                            <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-3">
                                <i className="fas fa-location-dot mt-0.5 text-gray-400" />

                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-800">{locationAddress}</p>

                                    {locationDescription && (
                                        <p className="mt-1 text-xs leading-5 text-gray-500">
                                            {locationDescription}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ====================================================
                    DESCRIPTION
                ==================================================== */}

                        {description && (
                            <div className="mt-5 border-t border-gray-100 pt-4">
                                <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                    Description
                                </p>

                                <p className="rounded-lg bg-gray-50 p-3 text-sm leading-6 text-gray-600">
                                    {description}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            );
        };
        const navigate = useNavigate();

        const params = new URLSearchParams(location.search);
        const modeQuery = params.get("mode");

        const { t } = useTranslation("service_request_home");

        const [downloadAllData, setDownloadAllData] = useState<any[]>([]);
        const [downloading, setDownloading] = useState(false);

        const [serviceRequestList, setServiceRequestList] = useState<any[]>([]);

        const displayedServiceRequestList =
            Array.isArray(faceSelectedRequest) && faceSelectedRequest.length > 0
                ? faceSelectedRequest
                : serviceRequestList;

        const [selectedRowId, setSelectedRowId] = useState<number | null>(null);

        /* ============================================================
         * BENEFICIARY REQUESTS OVERLAY
         * ============================================================ */

        const [beneficiaryRequests, setBeneficiaryRequests] = useState<any[]>([]);
        const [requestsLoading, setRequestsLoading] = useState(false);
        const [requestsOverlayOpen, setRequestsOverlayOpen] = useState(false);
        const [selectedBeneficiary, setSelectedBeneficiary] = useState<any>(null);
        const [selectedBeneficiaryRequest, setSelectedBeneficiaryRequest] = useState<any>(null);
        // const isMyRequests = mode === "MY_REQUESTS";
        const isMyRequests = modeQuery === "my";

        const isEmptyFilter = Object.values(activeServiceFilter).every((v) =>
            Array.isArray(v) ? v.length === 0 : !v
        );

        /* ============================================================
         * FETCH ALL REQUESTS OF BENEFICIARY
         * ============================================================ */

        const fetchSRByVoterId = async (voterId: string) => {
            if (!voterId) {
                console.log("⚠️ No voter ID available for this service request");

                return null;
            }

            try {
                const response = await rootStore.apiRequestHandler(
                    "unified_service_request/sr-by-voter-id",
                    {
                        voterid: voterId,
                    },
                    t("couldnot_fetch_service_request_list")
                );

                console.log("🔥 SR BY VOTER ID RESPONSE:", response);

                return response;
            } catch (error) {
                console.error("❌ Failed to fetch SR by voter ID:", error);

                throw error;
            }
        };

        /* ============================================================
         * REQUESTS BUTTON CLICK
         * ============================================================ */

        const handleRequestsClick = async (item: any, e: React.MouseEvent) => {
            e.stopPropagation();

            const voterId = item?.subject?.subject?.profile?.voterid;

            if (!voterId) {
                console.warn("⚠️ No voter ID available");
                return;
            }

            setSelectedBeneficiary(item);
            setBeneficiaryRequests([]);
            setSelectedBeneficiaryRequest(null);
            setRequestsOverlayOpen(true);
            setRequestsLoading(true);

            try {
                const response = await fetchSRByVoterId(voterId);

                console.log("🔥 SR BY VOTER ID RESPONSE:", response);

                const requests = Array.isArray(response)
                    ? response
                    : Array.isArray(response?.data)
                      ? response.data
                      : // @ts-ignore
                        Array.isArray(response?.data?.result)
                        ? // @ts-ignore
                          response.data.result
                        : [];

                setBeneficiaryRequests(requests);

                // If only one request, automatically select it
                if (requests.length === 1) {
                    setSelectedBeneficiaryRequest(requests[0]);
                }

                // If multiple, initially select the first request
                // so the overlay is not empty.
                if (requests.length > 1) {
                    setSelectedBeneficiaryRequest(requests[0]);
                }
            } catch (error) {
                console.error("❌ Failed to load beneficiary requests:", error);

                setBeneficiaryRequests([]);
                setSelectedBeneficiaryRequest(null);
            } finally {
                setRequestsLoading(false);
            }
        };

        /* ============================================================
         * CLOSE REQUESTS OVERLAY
         * ============================================================ */

        const closeRequestsOverlay = () => {
            setRequestsOverlayOpen(false);

            // Optional cleanup
            setBeneficiaryRequests([]);
            setSelectedBeneficiary(null);
        };

        /* ============================================================
         * MAIN REQUEST LIST API
         * ============================================================ */

        const fetchRequests = async () => {
            const endpoint = isMyRequests
                ? "unified_service_request/my-service-request"
                : "unified_service_request/service-requests-search-filter";

            return await rootStore.apiRequestHandler(
                endpoint,
                {
                    projectid: Number(projectid),

                    status: activeServiceFilter?.status ?? [],

                    nodes: activeServiceFilter?.nodes ?? [],

                    priorities: activeServiceFilter?.priority ?? [],

                    locations: activeServiceFilter?.location ?? [],

                    assignees: activeServiceFilter?.assignee ?? [],

                    created_by: activeServiceFilter?.created_by ?? [],

                    types: activeServiceFilter?.type ?? [],

                    offset: (serviceRequestsStore.pageNumber - 1) * serviceRequestsStore.pageSize,

                    query: searchQuery,

                    count: serviceRequestsStore.pageSize,

                    constituency: activeServiceFilter?.constituency ?? [],
                },
                t("couldnot_fetch_service")
            );
        };

        /* ============================================================
         * MAIN REQUEST QUERY
         * ============================================================ */

        const { isLoading, isFetching } = useQuery({
            queryKey: [
                "service-requests",
                mode,
                projectid,
                searchQuery,
                activeServiceFilter,
                serviceRequestsStore.pageNumber,
                serviceRequestsStore.pageSize,
            ],

            queryFn: fetchRequests,

            onSuccess: (data: any) => {
                setServiceRequestList(data?.data?.result ?? []);

                serviceRequestsStore.setResultsCount(data?.data?.count);

                serviceRequestsStore.setResultsSize(data?.data?.total);
            },
        });

        /* ============================================================
         * AUDIT LOG QUERY
         * ============================================================ */

        useQuery({
            queryKey: ["sr-audit", selectedRowId],

            enabled: !!selectedRowId && !isMyRequests,

            queryFn: async () => {
                return await rootStore.apiRequestHandler(
                    "unified_service_request/service-requests-audit-logs",
                    {
                        sr_id: selectedRowId,
                    },
                    t("couldnot_fetch_service_request_list")
                );
            },

            onSuccess: (data: any) => {
                serviceRequestsStore.setActiveLogs(data?.data);
            },
        });

        /* ============================================================
         * TABLE ACTION BUTTONS
         * ============================================================ */

        const actionButtons = (params: any) => (
            <div className="flex flex-row items-center justify-center gap-2">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();

                        serviceRequestsStore.setActiveServiceRequest(params.data);

                        isMyRequests ? navigate("../editrequest") : navigate("editrequest");
                    }}
                    sx={{
                        color: "#3B82F6",
                        "&:hover": {
                            backgroundColor: "#EFF6FF",
                        },
                    }}
                >
                    <i className="fas fa-edit text-sm" />
                </IconButton>

                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();

                        serviceRequestsStore.setActiveServiceRequest(params.data);

                        serviceRequestsStore.update_modal_status("deleteServiceRequest", true);
                    }}
                    sx={{
                        color: "#EF4444",
                        "&:hover": {
                            backgroundColor: "#FEF2F2",
                        },
                    }}
                >
                    <i className="fas fa-trash text-sm" />
                </IconButton>
            </div>
        );

        /* ============================================================
         * TABLE ROW CLICK
         * ============================================================ */

        const onRowClicked = (params: any) => {
            serviceRequestsStore.setActiveServiceRequest(params.data);

            setSelectedRowId(params.data.id);
        };

        const onCellClicked = (params: any) => {
            if (params.colDef.field !== "actions") {
                serviceRequestsStore.setshowServiceRequest(true);
            }

            params.event.stopPropagation();
        };

        const getRtcDetails = (item: any) => {
            return item?.rtc_details || item?.more?.rtc_details || "";
        };

        const hasRtcDetails = displayedServiceRequestList.some((item) => getRtcDetails(item).trim() !== "");
        /* ============================================================
         * TABLE COLUMNS
         * ============================================================ */

        const columnDefs = [
            ...(ENABLE_SAT_COLUMN
                ? [
                      {
                          headerName: "SAT",
                          field: "satisfaction",
                          width: 100,

                          cellRenderer: (params: any) => {
                              return (
                                  <div className="flex h-full items-center justify-center">
                                      <SatisfactionIcon requestId={params.data.id} size="medium" />
                                  </div>
                              );
                          },

                          suppressMovable: true,
                          suppressMenu: true,
                          pinned: "left" as const,
                      },
                  ]
                : []),

            {
                headerName: t("srtable_status"),
                field: "status.status",
                width: 160,

                cellRenderer: (params: any) => {
                    return <StatusBadge status={params.value} size="small" />;
                },
            },

            {
                headerName: t("srtable_trackingid"),
                field: "trackingid",
                width: 140,

                cellRenderer: (params: any) => (
                    <span className="font-mono text-sm font-semibold text-gray-700">{params.value}</span>
                ),
            },

            {
                headerName: t("srtable_name"),
                field: "subject.subject.profile.name_eng",
                width: 200,

                cellRenderer: (params: any) => (
                    <div className="py-1">
                        <p className="font-medium text-gray-900">{params.value || "N/A"}</p>

                        <p className="text-xs text-gray-500">
                            {params.data.subject?.subject?.whatsapp || "No phone"}
                        </p>
                    </div>
                ),
            },

            {
                headerName: "Booth / Address",
                colId: "booth_address",
                width: 220,

                valueGetter: (params: any) => {
                    const booth =
                        params.data?.subject?.additional?.booth ||
                        params.data?.subject?.subject?.additional?.booth ||
                        "";

                    const address =
                        params.data?.subject?.subject?.profile?.address || "";

                    return { booth, address };
                },

                cellRenderer: (params: any) => {
                    const booth = params.value?.booth;
                    const address = params.value?.address;

                    return (
                        <div className="py-1">
                            <p className="text-sm font-medium text-gray-800">
                                {booth ? `Booth: ${booth}` : "Booth: N/A"}
                            </p>

                            <p className="line-clamp-2 text-xs text-gray-500">
                                {address || "No address"}
                            </p>
                        </div>
                    );
                },
            },

            {
                headerName: t("srtable_request"),
                field: "type.type.name",
                flex: 1,
                minWidth: 200,

                cellRenderer: (params: any) => (
                    <p className="line-clamp-2 text-gray-800">{params.value || "N/A"}</p>
                ),
            },

            {
                headerName: t("srtable_assigned"),
                field: "assignee",
                width: 180,

                cellRenderer: (data: any) => {
                    const v = data.value;

                    const name = v?.is_office_person ? v?.user_profile?.name : v?.assignee?.name;

                    return (
                        <div className="flex items-center gap-2">
                            <i className="fas fa-user-circle text-gray-400" />

                            <p className="font-medium text-gray-700">{name || "Unassigned"}</p>
                        </div>
                    );
                },
            },

            {
                headerName: t("srtable_assigned_by"),
                field: "created_by",
                width: 160,

                cellRenderer: (data: any) => (
                    <p className="font-medium text-gray-700">{data?.value?.name || "N/A"}</p>
                ),
            },

            {
                headerName: t("srtable_date"),
                field: "created",
                width: 180,

                cellRenderer: (params: any) => {
                    const created = dayjs(params.value);

                    const updated = dayjs(params.data.modified);

                    const daysAgo = dayjs().diff(updated, "day");

                    return (
                        <div className="py-1">
                            <p className="text-sm font-medium text-gray-800">
                                {created.format("MMM D, YYYY")}
                            </p>

                            <p className="text-xs text-gray-500">
                                {t("date_update")} {daysAgo} {t("days")}
                            </p>
                        </div>
                    );
                },
            },
            ...(hasRtcDetails
                ? [
                      {
                          headerName: "RTC Details",
                          colId: "rtc_details",
                          width: 220,

                          valueGetter: (params: any) => {
                              return getRtcDetails(params.data);
                          },

                          cellRenderer: (params: any) => {
                              const rtcDetails = params.value;

                              return rtcDetails ? (
                                  <div className="flex h-full items-center">
                                      <span className="text-sm font-medium text-gray-800">{rtcDetails}</span>
                                  </div>
                              ) : (
                                  <span className="text-sm text-gray-400">N/A</span>
                              );
                          },
                      },
                  ]
                : []),

            {
                headerName: t("srtable_actions"),
                field: "actions",
                width: 120,

                cellRenderer: actionButtons,

                suppressMovable: true,
                suppressMenu: true,
                suppressClickEdit: true,
            },
        ];

        /* ============================================================
         * LOADING STATE
         * ============================================================ */

        const showSkeleton = isLoading || (isFetching && serviceRequestList.length === 0);

        /* ============================================================
         * RENDER
         * ============================================================ */

        // @ts-ignore
        return (
            <>
                {!isMyRequests && !isEmptyFilter && <ServiceFilteredBy />}

                {Array.isArray(faceSelectedRequest) && faceSelectedRequest.length > 0 && (
                    <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
                        <div className="flex items-center gap-2">
                            <i className="fas fa-face-viewfinder text-blue-600" />

                            <div>
                                <p className="text-sm font-semibold text-blue-900">
                                    Face matched service requests
                                </p>

                                <p className="text-xs text-blue-700">
                                    Showing {faceSelectedRequest.length} service request
                                    {faceSelectedRequest.length > 1 ? "s" : ""} matched with the captured
                                    face.
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            onClick={onClearFaceFilter}
                            className="rounded-md border border-blue-200 bg-white px-3 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-100"
                        >
                            Show All Requests
                        </button>
                    </div>
                )}

                {/* ====================================================
                    MAIN CONTENT
                ==================================================== */}

                {showSkeleton ? (
                    viewMode === "card" ? (
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {Array.from({ length: 6 }, (_, i) => (
                                <ServiceRequestCardSkeleton key={i} />
                            ))}
                        </div>
                    ) : (
                        <ServiceRequestTableSkeleton />
                    )
                ) : displayedServiceRequestList.length === 0 ? (
                    <div className="flex flex-col items-center justify-center px-4 py-16">
                        <div className="mb-4 rounded-full bg-gray-100 p-6">
                            <i className="fas fa-inbox text-5xl text-gray-400" />
                        </div>

                        <h3 className="mb-2 text-xl font-semibold text-gray-700">
                            No Service Requests Found
                        </h3>

                        <p className="max-w-md text-center text-gray-500">
                            {isEmptyFilter
                                ? "There are no service requests to display. Create a new request to get started."
                                : "No results match your current filters. Try adjusting your search criteria."}
                        </p>
                    </div>
                ) : viewMode === "card" ? (
                    /* =================================================
                           CARD VIEW
                        ================================================= */

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {displayedServiceRequestList.map((item) => (
                            <ServiceRequestCard
                                key={item.id}
                                data={item}
                                onEdit={() => {
                                    serviceRequestsStore.setActiveServiceRequest(item);

                                    isMyRequests ? navigate("../editrequest") : navigate("editrequest");
                                }}
                                onDelete={() => {
                                    serviceRequestsStore.setActiveServiceRequest(item);

                                    serviceRequestsStore.update_modal_status("deleteServiceRequest", true);
                                }}
                                onClick={() => {
                                    serviceRequestsStore.setActiveServiceRequest(item);
                                    setSelectedRowId(item.id);

                                    // Existing detail side overlay
                                    serviceRequestsStore.setshowServiceRequest(true);
                                }}
                                onRequestsClick={(e) => handleRequestsClick(item, e)}
                                isMyRequests={isMyRequests}
                            />
                        ))}
                    </div>
                ) : (
                    /* =================================================
                           TABLE VIEW
                        ================================================= */

                    <div
                        className="ag-theme-quartz overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
                        style={{
                            height: 600,
                            width: "100%",
                        }}
                    >
                        {/* @ts-ignore */}
                        <AgGridReact
                            rowData={displayedServiceRequestList}
                            columnDefs={columnDefs}
                            rowStyle={rowStyle}
                            onRowClicked={onRowClicked}
                            onCellClicked={onCellClicked}
                            suppressRowClickSelection
                            rowHeight={70}
                            headerHeight={50}
                        />
                    </div>
                )}

                {/* ====================================================
                    REQUESTS SIDE OVERLAY
                ==================================================== */}

                <SideOverlay
                    show={requestsOverlayOpen}
                    onClose={closeRequestsOverlay}
                    title="Beneficiary Requests"
                    size="large"
                >
                    {/* Beneficiary Header */}
                    {selectedBeneficiary && (
                        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="flex items-center justify-between gap-4">
                                <div className="min-w-0">
                                    <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                                        Beneficiary
                                    </p>

                                    <p className="mt-1 truncate text-base font-semibold text-gray-900">
                                        {selectedBeneficiary?.subject?.subject?.profile?.name_eng || "N/A"}
                                    </p>

                                    <p className="mt-1 text-sm text-gray-500">
                                        {selectedBeneficiary?.subject?.subject?.whatsapp || "No phone"}
                                    </p>
                                </div>

                                <div className="shrink-0 text-right">
                                    <p className="text-xs font-medium uppercase tracking-wide text-gray-400">
                                        Voter ID
                                    </p>

                                    <p className="mt-1 font-mono text-xs font-semibold text-gray-700">
                                        {selectedBeneficiary?.subject?.subject?.profile?.voterid || "N/A"}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {requestsLoading ? (
                        /* Loading */
                        <div className="flex min-h-[300px] flex-col items-center justify-center">
                            <div className="mb-3 h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />

                            <p className="text-sm font-medium text-gray-600">Loading requests...</p>
                        </div>
                    ) : beneficiaryRequests.length === 0 ? (
                        /* Empty */
                        <div className="flex min-h-[300px] flex-col items-center justify-center">
                            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                                <i className="fas fa-inbox text-xl text-gray-400" />
                            </div>

                            <p className="text-sm font-semibold text-gray-700">No requests found</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {/* =====================================================
                MULTIPLE REQUESTS
            ===================================================== */}

                            {beneficiaryRequests.length > 1 && (
                                <div>
                                    <div className="mb-2 flex items-center justify-between">
                                        <p className="text-sm font-semibold text-gray-700">Requests</p>

                                        <span className="rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700">
                                            {beneficiaryRequests.length}
                                        </span>
                                    </div>

                                    <div className="flex gap-2 overflow-x-auto pb-2">
                                        {beneficiaryRequests.map((request: any, index: number) => {
                                            const isSelected = selectedBeneficiaryRequest?.id === request?.id;

                                            const requestName =
                                                request?.type?.type?.name || "Service Request";

                                            const trackingId = request?.trackingid || "N/A";

                                            const status = request?.status?.status || "N/A";

                                            return (
                                                <button
                                                    key={request?.id ?? index}
                                                    type="button"
                                                    onClick={() => setSelectedBeneficiaryRequest(request)}
                                                    className={`min-w-[180px] rounded-xl border p-3 text-left transition-all duration-200 ${
                                                        isSelected
                                                            ? "border-blue-500 bg-blue-50 shadow-sm"
                                                            : "border-gray-200 bg-white hover:border-blue-200 hover:bg-gray-50"
                                                    } `}
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <p className="line-clamp-2 text-xs font-semibold text-gray-900">
                                                            {requestName}
                                                        </p>

                                                        {isSelected && (
                                                            <i className="fas fa-check-circle shrink-0 text-xs text-blue-600" />
                                                        )}
                                                    </div>

                                                    <p className="mt-2 font-mono text-[10px] text-gray-500">
                                                        {trackingId}
                                                    </p>

                                                    <div className="mt-2">
                                                        <StatusBadge status={status} size="small" />
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* =====================================================
                SELECTED REQUEST DETAIL
            ===================================================== */}

                            {selectedBeneficiaryRequest && (
                                <div>
                                    {beneficiaryRequests.length > 1 && (
                                        <p className="mb-2 text-sm font-semibold text-gray-700">
                                            Request Details
                                        </p>
                                    )}

                                    <RequestDetail request={selectedBeneficiaryRequest} />
                                </div>
                            )}
                        </div>
                    )}
                </SideOverlay>

                {/* ====================================================
                    DELETE MODAL
                ==================================================== */}

                <ServiceRequestDelete />
            </>
        );
    }
);
